import { useMsal } from "@azure/msal-react";
import { useEffect, useState, useMemo, useCallback } from "react";
import ApiProvider from "../../apiProvider.js";
import SkillsExperience from "../skills-experience/skills-experience";
import SkillsLanding from "../skills-experience/skills-landing";
import ProfileLanding from "../profile/profile-landing";
import PersonalInfo from "../profile/info/personalInfo";
import LicensePlates from "../profile/license/licensePlates.jsx";
import { CdsSiteframe } from "clayco-component-library/lib";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Payslips from "../payroll/payslips/payslip.jsx";
import TaxStatements from "../payroll/taxStatements/taxStatement.jsx";
import StayBenefitHome from "../payroll/staybenefit/StayBenefitHome.jsx";
import HouseholdItemsHome from "../payroll/householditems/HouseholdItemsHome.jsx";
import Spinner from "react-bootstrap/Spinner";
import AppIcon from "../../assets/EmployeePortal_White.svg";
import EmployeeHelper from "../../Components/employeeUtility.jsx";
import HomeLanding from "./home-landing.jsx";
import BuildMenu from "../../menuBuilder.js";
import { useEmployeeNumber } from "../../employeeNumber";
import ManagementView from "../skills-experience/management/management-view.jsx";
import VideoPlayer from "../video-player/video-player.jsx";
import PayrollLanding from "../payroll/payroll-landing.jsx";
import useVisibilityStore from "../../store/visibilityStore";
import Contacts from "../profile/contacts/contacts.jsx";
import AdminView from "../profile/admin/adminView.jsx";
import ContactVerification from "../profile/info/contactVerification.jsx";
import AdminReview from "../profile/admin/adminReview.jsx";
import { use } from "react";

// An API without loadHouseholdItems predates the Household Items access list; fall back to the
// Stay Benefit rule the page used to share so job family access is unchanged.
const householdItemsVisible = (rules) => rules?.loadHouseholdItems ?? rules?.loadStayBenefit ?? false;

const Home = () => {
    const { instance, accounts } = useMsal();
    const { visibilityRules, fetchVisibilityRules, loading: visibilityLoading } = useVisibilityStore();

    const apiProvider = useMemo(() => new ApiProvider(instance, accounts[0]), [instance, accounts]);

    const [status, setStatus] = useState({ completed: false });
    const [statusLoading, setStatusLoading] = useState(true);
    const [skipStepHistory, setSkipStepHistory] = useState({});
    const [navItems, setNavItems] = useState([]);
    const [location, setLocation] = useState("");
    const [loadSkillsExperience, setLoadSkillsExperience] = useState(false);
    const [loadPreviousProjects, setLoadPreviousProjects] = useState(false);
    const [loadLicensePlates, setLoadLicensePlates] = useState(false);
    const [loadPayslips, setLoadPayslips] = useState(false);
    const [loadOboControl, setLoadOboControl] = useState(false);
    const [loadOboManager, setLoadOboManager] = useState(false);
    const [loadProfileAdmin, setLoadProfileAdmin] = useState(false);
    const [loadContactAdmin, setLoadContactAdmin] = useState(false); // new flag for Emergency Contacts OBO
    const [loadStayBenefit, setLoadStayBenefit] = useState(false); // new flag for Stay Benefit page
    const [loadHouseholdItems, setLoadHouseholdItems] = useState(false);
    const [brand, setBrand] = useState("clayco");
    const [userFirstName, setUserGivenName] = useState("");
    const {employeeNumber} = useEmployeeNumber();
    const [rulesLoaded, setRulesLoaded] = useState(false); // New state for visibility rules loading
    const [visibilityRulesError, setVisibilityRulesError] = useState(null); // New state for error message
    const navigate = useNavigate();

    const onLogout = () => {
        console.debug("logout");
        instance.logoutRedirect({postLogoutRedirectUri: window.location.href}).catch(e => {
            console.error(e);
        });
    } 


    // Handle visibility rules updates
    useEffect(() => {
        if (!visibilityRules) {
            console.warn("Visibility rules are null. Using default values.");
            setLoadSkillsExperience(false);
            setLoadPreviousProjects(false);
            setLoadPayslips(false);
            setLoadLicensePlates(false);
            setLoadOboControl(false);
            setLoadOboManager(false);
            setLoadProfileAdmin(false);
        } else {
            setLoadSkillsExperience(visibilityRules.loadSkillsExperience);
            setLoadPreviousProjects(visibilityRules.loadPreviousProjects);
            setLoadPayslips(visibilityRules.loadPayslipsAndTaxes);
            setLoadLicensePlates(visibilityRules.showLicensePlates);
            setLoadOboControl(visibilityRules.loadOnBehalfOfControl);
            setLoadOboManager(visibilityRules.loadOnBehalfOfManager);
            setLoadProfileAdmin(visibilityRules.loadPersonalInfoAdmin);
            setLoadContactAdmin(visibilityRules.loadContactAdmin || false);
            setLoadStayBenefit(visibilityRules.loadStayBenefit || false);
            setLoadHouseholdItems(householdItemsVisible(visibilityRules));

            if(visibilityRules.loadOnBehalfOfControl)
            {
                setLoadPreviousProjects(true);
                setLoadSkillsExperience(true);
            }
        }
    }, [visibilityRules]);

    useEffect(() => {
        getUserProfile();
    }, []);

    useEffect(() => {
        async function GetData() {
            //if (!status) {
                //Get has completed skills/experience
                let result = await apiProvider.Get(
                    `Status/`,
                    null,
                    "application/json",
                    false
                );

                if (
                    result &&
                    !result.error &&
                    result !== "" &&
                    result.length !== 0
                ) {
                    setStatus(result);
                    localStorage.setItem("submitComplete", result.completed);
                } else {
                    localStorage.setItem("submitComplete", false);
                    setStatus({ completed: false });
                }

                if (visibilityRules?.loadPreviousProjects) {
                    if (window.location.pathname === "/Skills/previousProjects") {
                        handleInternalNavigation("/Home");
                    }
                }

                setStatusLoading(false);
            //}

            const skip = JSON.parse(localStorage.getItem("skipStep"));
            if (skip) {
                setSkipStepHistory(skip);
            }
        }

        GetData();
    }, [employeeNumber]);

    useEffect(() => {
        localStorage.setItem("skipStep", JSON.stringify(skipStepHistory));
    }, [skipStepHistory]);

    const getUserProfile = async () => {
        // determine if b2c, otherwise use existing method
        let company = undefined;
        company = accounts[0].idTokenClaims?.extension_Company;
        let name = accounts[0].idTokenClaims?.given_name;
        setUserGivenName(name);

        if (company === undefined){
            const token = await getAccessToken();
            if (!token) {
                return null;
            }
            const headers = new Headers();
            const bearer = `Bearer ${token}`;

            headers.append("Authorization", bearer);

            const options = {
                method: "GET",
                headers: headers,
            };

            var response = await fetch(
                "https://graph.microsoft.com/beta/me",
                options
            );

            let profile = await response.json();
            setUserGivenName(profile?.givenName);

            company = profile?.companyName;
        }

        switch (company.toLowerCase()) {
            case "csi":
            case "concrete strategies":
                setBrand("csi");
                break;
            case "ljc":
            case "lamar johnson collaborative":
                setBrand("ljc");
                break;
            case "crg":
                setBrand("crg");
                break;
            case "ventana":
                setBrand("ventana");
                break;
            default:
                setBrand("clayco");
                break;
        }
    };

    const getAccessToken = async () => {
        const request = {
            scopes: ["profile", "User.Read"],
            account: accounts[0],
        };
        try {
            return (await instance.acquireTokenSilent(request)).accessToken;
        } catch {
            try {
                return (await instance.acquireTokenPopup(request)).accessToken;
            } catch {
                return null;
            }
        }
    };

    useEffect(() => {
        async function loadVisibilityRules() {
            try {
                // Fetch visibility rules
                await fetchVisibilityRules(apiProvider);

                // Retrieve the updated visibility rules from the store
                const updatedVisibilityRules = useVisibilityStore.getState().visibilityRules;

                if (!updatedVisibilityRules) {
                    console.warn("Visibility rules are null. Using default values.");
                    setLoadSkillsExperience(false);
                    setLoadPreviousProjects(false);
                    setLoadPayslips(false);
                    setLoadLicensePlates(false);
                    setLoadOboControl(false);
                    setLoadOboManager(false);
                    setLoadProfileAdmin(false);
                    setVisibilityRulesError("Failed to load your account permissions. Please try again later.");
                } else {
                    // Update state with the fetched rules
                    setLoadSkillsExperience(updatedVisibilityRules.loadSkillsExperience || false);
                    setLoadPreviousProjects(updatedVisibilityRules.loadPreviousProjects || false);
                    setLoadPayslips(updatedVisibilityRules.loadPayslipsAndTaxes || false);
                    setLoadLicensePlates(updatedVisibilityRules.showLicensePlates || false);
                    setLoadOboControl(updatedVisibilityRules.loadOnBehalfOfControl || false);
                    setLoadOboManager(updatedVisibilityRules.loadOnBehalfOfManager || false);
                    setLoadProfileAdmin(updatedVisibilityRules.loadPersonalInfoAdmin || false);
                    setLoadContactAdmin(updatedVisibilityRules.LoadContactAdmin || updatedVisibilityRules.loadContactAdmin || false);
                    setLoadStayBenefit(updatedVisibilityRules.loadStayBenefit || false);
                    setLoadHouseholdItems(householdItemsVisible(updatedVisibilityRules));
                    
                    if(updatedVisibilityRules.loadOnBehalfOfControl)
                    {
                        setLoadPreviousProjects(true);
                        setLoadSkillsExperience(true);
                    }
                }

                // Mark rules as loaded
                setRulesLoaded(true);
            } catch (error) {
                console.error("Error loading visibility rules:", error);

                // Set default values in case of an error
                setLoadSkillsExperience(false);
                setLoadPreviousProjects(false);
                setLoadPayslips(false);
                setLoadLicensePlates(false);
                setLoadOboControl(false);
                setLoadOboManager(false);
                setLoadStayBenefit(false);
                setLoadHouseholdItems(false);

                setRulesLoaded(true); // Mark as loaded to avoid infinite loading

                // Set error message
                setVisibilityRulesError("Failed to Your account permissions. Please try again later.");
            }
        }

        loadVisibilityRules();
        
    }, [fetchVisibilityRules, apiProvider]);

    function handleInternalNavigation(url) {
        if (url !== location) { // Only reset and fetch rules if navigating to a new location
            setLocation(url);

            navigate(url);
        }
    }

    function setMenuOptions(location, overrideLoadSkillsExperience = null, overrideLoadPreviousProjects = null, overrideStatus = null, overrideLoadStayBenefit = null, overridePayrollAdmin = null, overrideLoadHouseholdItems = null) {
        let menu = [];
        
        const payrollAdmin = overridePayrollAdmin !== null
            ? overridePayrollAdmin
            : (accounts[0]?.idTokenClaims?.roles?.includes("PayrollAdmin") || false);
        if (
            overrideLoadSkillsExperience !== null &&
            overrideLoadPreviousProjects !== null &&
            overrideStatus !== null &&
            overrideLoadStayBenefit !== null
        ) {
            menu = BuildMenu(
                overrideLoadSkillsExperience,
                overrideLoadPreviousProjects,
                loadLicensePlates,
                loadPayslips,
                overrideStatus,
                loadOboManager,
                loadProfileAdmin,
                overrideLoadStayBenefit,
                payrollAdmin,
                overrideLoadHouseholdItems !== null ? overrideLoadHouseholdItems : loadHouseholdItems
            );
        } else {
            menu = BuildMenu(
                loadSkillsExperience,
                loadPreviousProjects,
                loadLicensePlates,
                loadPayslips,
                status.completed,
                loadOboManager,
                loadProfileAdmin,
                loadStayBenefit,
                payrollAdmin,
                loadHouseholdItems
            );
        }
        setNavItems(menu);
    }

    async function menuOverride(location) {
        // Get display and loading statuses
        let loadSkills = await EmployeeHelper.CheckLoadSkillsExperience(apiProvider);
        let loadPrevProj = await EmployeeHelper.CheckLoadPreviousProjects(apiProvider);
        let visibilityRules = await apiProvider.Get("Employee/GetVisibilityRules");
        let result = await apiProvider.Get(
            `Status/`,
            null,
            "application/json",
            false
        );
        // Determine StayBenefit and PayrollAdmin
        const stayBenefit = visibilityRules.loadStayBenefit || false;
        const householdItems = householdItemsVisible(visibilityRules);
        const payrollAdmin = accounts[0]?.idTokenClaims?.roles?.includes("PayrollAdmin") || false;
        console.log("menuOverride: ", loadSkills, loadPrevProj, visibilityRules, stayBenefit, payrollAdmin);
        setMenuOptions(
            location,
            visibilityRules.loadSkillsExperience,
            visibilityRules.loadPreviousProjects,
            result.completed,
            stayBenefit,
            payrollAdmin,
            householdItems
        );
    }
    
    return (
        <CdsSiteframe
            title="Employee Portal"
            navItems={navItems}
            handleNavigate={handleInternalNavigation}
            brand={brand}
            icon={AppIcon}
            homeLink="/Home"
            useIdentity
            activeLinkParameter="step"
            location={location}
            grayBackground={true}
            showSettingsMenu={true}
            showIdentity={true}
            user={accounts[0]?.name}
            onLogout={onLogout}
        >
            {statusLoading || !rulesLoaded ? (
                <div className="spinner home-spinner">
                    <Spinner />
                    <p>Loading...</p>
                </div>
            ) : visibilityRulesError ? ( // Display error message if fetch fails
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "150px",
                    textAlign: "center",
                    color: "#B00020",
                    backgroundColor: "#FFF4F4",
                    borderRadius: "4px",
                    padding: "1rem",
                    fontWeight: "500",
                }}>
                    <p>{visibilityRulesError}</p>
                </div>
            ) : (
                <Routes>
                    <Route path="/" element={<Navigate to="/Home" />} />
                    <Route path="/Verification/:type/:guid" element={<ContactVerification />} />
                    <Route path="/Home" element={
                        <HomeLanding
                            currentLocation={location}
                            onTileClick={handleInternalNavigation}
                            userName={userFirstName}
                            showPayslips={loadPayslips}
                            showSkillsExp={loadSkillsExperience}
                            setMenuOptions={setMenuOptions}
                            setLocation={setLocation}
                        />
                    } />
                    <Route path="/ProfileLanding" element={
                        <ProfileLanding
                            userName={userFirstName}
                            profileAdmin={loadProfileAdmin}
                            contactAdmin={loadContactAdmin}
                            showLicensePlates={loadLicensePlates}
                            setLocation={handleInternalNavigation}
                            setMenuOptions={setMenuOptions}
                        />
                    } />
                    <Route path="/Profile/Info" element={<PersonalInfo setMenuOptions={setMenuOptions} />} />
                    <Route path="/Profile/Contacts" element={<Contacts setMenuOptions={setMenuOptions} />} />
                    <Route path="/Profile/Plates" element={<LicensePlates setMenuOptions={setMenuOptions} />} />
                    <Route path="/Profile/admin" element={<AdminView setMenuOptions={setMenuOptions} />} />
                    <Route path="/Profile/admin/:id" element={<AdminReview setMenuOptions={setMenuOptions} />} />
                    <Route path="/PayrollLanding" element={
                        <PayrollLanding
                            userName={userFirstName}
                            showPayroll={loadPayslips}
                            // Show Stay Benefit tile if visibility rule allows OR user is PayrollAdmin (matches menuBuilder logic)
                            showStayBenefit={loadStayBenefit || (accounts[0]?.idTokenClaims?.roles?.includes("PayrollAdmin") || false)}
                            showHouseholdItems={loadHouseholdItems || (accounts[0]?.idTokenClaims?.roles?.includes("PayrollAdmin") || false)}
                            setLocation={handleInternalNavigation}
                            setMenuOptions={setMenuOptions}
                        />
                    } />
                    <Route path="/Payslips" element={
                        <Payslips
                            currentLocation={location}
                            setMenuOptions={setMenuOptions}
                        />
                    } />
                    <Route path="/TaxStatements" element={
                        <TaxStatements
                            currentLocation={location}
                            setMenuOptions={setMenuOptions}
                        />
                    } />
                    <Route path="/StayBenefit" element={<StayBenefitHome setMenuOptions={setMenuOptions} />} />
                    {/* Route variants for Household Items */}
                    <Route path="/HouseholdItems" element={<HouseholdItemsHome setMenuOptions={setMenuOptions} />} />
                    <Route path="/SkillsLanding" element={
                        <SkillsLanding
                            userName={userFirstName}
                            setLocation={handleInternalNavigation}
                            setMenuOptions={setMenuOptions}
                            status={status}
                            loadOboControl={loadOboControl}
                            loadPreviousProjects={loadPreviousProjects}
                        />
                    } />
                    <Route path="/Skillsmanagement" element={<ManagementView setMenuOptions={setMenuOptions} />} />
                    <Route path="/Skills/*" element={
                        <SkillsExperience
                            loadPreviousProjects={loadPreviousProjects}
                            setLocation={handleInternalNavigation}
                            status={status}
                            statusLoading={statusLoading}
                            navItems={navItems}
                            skipStepHistory={skipStepHistory}
                            setSkipStepHistory={setSkipStepHistory}
                            setMenuOptions={setMenuOptions}
                            setMenuOverride={menuOverride}
                            loadOboControl={loadOboControl}
                        />
                    } />
                    <Route path="/VideoPlayer" element={<VideoPlayer />} />
                    <Route path="/VideoPlayer/:videoUrl" element={<VideoPlayer />} />
                </Routes>
            )}
        </CdsSiteframe>
    );
};

export default Home;
